import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useListCrewsQuery, useScheduleJobMutation, useGetSchedulingCalendarQuery,
  type CalendarDay, type ScheduleHistoryEntry,
} from '@/redux/internal-services/jobsApi';
import { Button } from '@/components/UI/Button';
import { CustomDatePicker, CustomTimePicker } from '@/components/UI/DateTimePickers';
import { daysSpanned, durationForDays } from '@/components/Jobs/jobDuration';

const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Python's date.weekday() is Monday=0..Sunday=6 — convert to a Sunday-first grid column.
const sundayCol = (pyWeekday: number) => (pyWeekday + 1) % 7;

const hasRainRisk = (day: CalendarDay) => {
  if (!day.weather?.short_forecast) return false;
  return /rain|storm|snow|shower/i.test(day.weather.short_forecast);
};

const DURATION_DAYS_OPTIONS = [
  { label: '1 day', days: 1 },
  { label: '2 days', days: 2 },
  { label: '3 days', days: 3 },
  { label: '4 days', days: 4 },
  { label: '5 days', days: 5 },
];

function addDays(dateStr: string, days: number) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days - 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}


const RESCHEDULE_REASONS = [
  'Weather', 'Execution interrupted', 'Corrective work / QA rework',
  'Crew unavailable', 'Customer request', 'Material delay',
];

const JobSchedulingForm: React.FC<{
  engagementId: string;
  onScheduled?: () => void;
  onCancel?: () => void;
  isReschedule?: boolean;
  currentSchedule?: {
    date: string; time: string; durationDays: number; crewName: string;
  } | null;
  scheduleHistory?: ScheduleHistoryEntry[];
}> = ({ engagementId, onScheduled, onCancel, isReschedule, currentSchedule, scheduleHistory }) => {
  const { t } = useTranslation();
  const { data: crews, isLoading: crewsLoading } = useListCrewsQuery();
  const [scheduleJob, { isLoading: isSaving }] = useScheduleJobMutation();

  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const [crewId, setCrewId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(fmt(now));
  const [startTime, setStartTime] = useState('08:00');
  const [durationDays, setDurationDays] = useState(1);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState<{ scheduled_date: string; start_time: string }[]>([]);
  const [blocked, setBlocked] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  // Two-click range selection: first click = start date, second click = end date.
  const [pickMode, setPickMode] = useState<'start' | 'end'>('start');
  const [endDate, setEndDate] = useState(fmt(now));
  // Reschedule-mode extras.
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');

  const { data: calendar, isLoading: calendarLoading } = useGetSchedulingCalendarQuery(
    { engagementId, year: viewYear, month: viewMonth, crewId: crewId || undefined },
    { skip: !engagementId },
  );

  // SAM-recommended windows from calendar data
  const suggestions = useMemo(() => {
    if (!calendar) return [];
    const out: { day: CalendarDay; label: string }[] = [];
    for (const day of calendar.days) {
      if (day.customer_unavailable_reason || day.customer_no_work) continue;
      if (day.crews_free_count <= 0) continue;
      if (hasRainRisk(day)) continue;
      const bookedCrewIds = new Set(day.bookings.map((b) => b.crew_id));
      const freeCrewNames = calendar.crews.filter((c) => !bookedCrewIds.has(c.crew_id)).map((c) => c.name);
      const weatherNote = day.weather?.short_forecast ? ` · ${day.weather.short_forecast.toLowerCase()}` : ' · clear';
      const crewNote = freeCrewNames.length === calendar.crews.length ? 'all crews free' : `${freeCrewNames.join(' + ')} free`;
      const dateLabel = new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      out.push({ day, label: `${dateLabel} · ${crewNote}${weatherNote}` });
    }
    return out.slice(0, 3);
  }, [calendar]);

  const crewBookedDays = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const day of calendar?.days ?? []) {
      for (const b of day.bookings) counts[b.crew_id] = (counts[b.crew_id] ?? 0) + 1;
    }
    return counts;
  }, [calendar]);

  const changeMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setViewMonth(m); setViewYear(y);
  };

  const handleSubmit = async (acknowledgeConflict = false) => {
    if (!crewId) { setError(t('jobs.selectCrewRequired', 'Select a Crew.')); return; }
    if (isReschedule && !rescheduleReason) { setError(t('jobs.rescheduleReasonRequired', 'Select a reason for rescheduling.')); return; }
    setError('');
    const reasonText = isReschedule
      ? [rescheduleReason, rescheduleNote.trim()].filter(Boolean).join(' — ')
      : undefined;
    try {
      const result = await scheduleJob({
        engagementId, scheduled_date: scheduledDate, start_time: startTime,
        duration_minutes: durationForDays(durationDays), crew_id: crewId,
        reason: reasonText,
        acknowledge_conflict: acknowledgeConflict,
      }).unwrap();
      setBlocked(false);
      if (result.conflicts.length > 0) setConflicts(result.conflicts);
      setConfirmed(true);
      onScheduled?.();
    } catch (err) {
      const e = err as { status?: number; data?: { message?: string } };
      if (e.status === 409) {
        setBlocked(true);
        setError(e.data?.message || t('jobs.scheduleBlocked', 'This Crew already has an overlapping job at that time.'));
        return;
      }
      setError(e.data?.message || t('jobs.scheduleFailed', 'Could not schedule the Job. Nothing has been changed.'));
    }
  };

  const selectedCrew = (crews ?? []).find((c) => c.crew_id === crewId);
  const selectedDay = calendar?.days.find((d) => d.date === scheduledDate);
  const isEarlyStart = startTime < '08:00';

  const fmtHistoryDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return iso; }
  };
  const fmtScheduleLine = (entry: ScheduleHistoryEntry) => {
    const parts: string[] = [];
    if (entry.scheduled_date) parts.push(new Date(entry.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }));
    if (entry.start_time) parts.push(entry.start_time);
    if (entry.duration_minutes) parts.push(`${daysSpanned(entry.start_time, entry.duration_minutes)} day(s)`);
    return parts.join(' · ') || '—';
  };

  return (
    <div className="space-y-4">
      {/* RESCHEDULE — current schedule + reason + history */}
      {isReschedule && (
        <div className="space-y-3">
          {/* Current schedule banner */}
          {currentSchedule && (
            <div className="rounded-[10px] border border-[#E8C98F] bg-[#FBF0DC] px-3 py-3">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-[11px] font-bold tracking-wide text-[#8A5A06] uppercase">
                  {t('jobs.currentSchedule', 'Current Schedule')}
                </span>
                <span className="text-[12.5px] text-[#8A5A06]">
                  {currentSchedule.date} · {currentSchedule.time} · {currentSchedule.durationDays} day(s) · {currentSchedule.crewName}
                </span>
              </div>
              <p className="mt-1.5 text-[11.5px] text-[#8A5A06] leading-relaxed">
                {t('jobs.rescheduleHint', 'Change the date, start time, duration, crew, or any combination. Project Manager only.')}
              </p>
              {/* Reason label */}
              <p className="mt-3 text-[11px] font-bold tracking-wide text-[#8A5A06] uppercase">
                {t('jobs.rescheduleReason', 'Reason')}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {RESCHEDULE_REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRescheduleReason(r === rescheduleReason ? '' : r)}
                    className={`rounded-full border px-3 py-1 text-[11.5px] font-semibold transition-colors ${
                      rescheduleReason === r
                        ? 'border-[#8A5A06] bg-[#8A5A06] text-white'
                        : 'border-[#E8C98F] bg-white text-[#8A5A06] hover:bg-[#FBF0DC]'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <textarea
                value={rescheduleNote}
                onChange={(e) => setRescheduleNote(e.target.value)}
                rows={2}
                placeholder={t('jobs.rescheduleNotePlaceholder', 'Reschedule note — what changed and who was informed (required for the audit trail)')}
                className="mt-2 w-full rounded-lg border border-[#E8C98F] bg-white px-3 py-2 text-[12px] text-ui-text focus:border-[#8A5A06] focus:outline-none resize-none"
              />
            </div>
          )}

          {/* Schedule history */}
          {scheduleHistory && scheduleHistory.length > 0 && (
            <div className="rounded-[10px] border border-ui-border px-3 py-3">
              <p className="mb-2 text-[11px] font-bold tracking-wide text-ui-text3 uppercase">
                {t('jobs.scheduleHistory', 'Schedule History')}
              </p>
              <div className="flex flex-col gap-2">
                {scheduleHistory.map((entry, i) => {
                  const isFirst = i === scheduleHistory.length - 1;
                  return (
                    <div key={i} className="border-l-2 border-[#D9BBFF] pl-2.5">
                      <div className="flex flex-wrap items-baseline gap-1.5">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${isFirst ? 'bg-[#F4EEFF] text-[#3F2D93]' : 'bg-[#FBF0DC] text-[#8A5A06]'}`}>
                          {isFirst ? t('jobs.historyScheduled', 'SCHEDULED') : t('jobs.historyRescheduled', 'RESCHEDULED')}
                        </span>
                        {entry.actor_name && (
                          <span className="text-[11.5px] text-ui-text3">{entry.actor_name}</span>
                        )}
                        <span className="text-[11.5px] text-ui-text3">{fmtHistoryDate(entry.changed_on)}</span>
                        {entry.reason && (
                          <span className="text-[11.5px] font-semibold text-[#8A5A06]">{entry.reason.split(' — ')[0]}</span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[11.5px] text-ui-text">{fmtScheduleLine(entry)}</p>
                      {entry.reason?.includes(' — ') && (
                        <p className="mt-0.5 text-[11.5px] text-ui-text3 italic">"{entry.reason.split(' — ').slice(1).join(' — ')}"</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SAM recommendations banner */}
      <div className="rounded-xl bg-brand-light/30 border border-brand-bright/20 p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-dark text-[10px] font-bold text-white shrink-0">S</span>
          <span className="text-xs font-semibold text-brand-dark">
            {t('jobs.samRecommendsWindows', 'SAM recommends these windows — crew free, forecast clear, customer available.')}
          </span>
        </div>
        {calendarLoading && (
          <p className="text-xs text-ui-text3">{t('common.loading', 'Loading…')}</p>
        )}
        {!calendarLoading && suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map(({ day, label }) => (
              <button
                key={day.date}
                type="button"
                onClick={() => {
                  setScheduledDate(day.date);
                  setEndDate(day.date);
                  setDurationDays(1);
                  setPickMode('end');
                  if (day.bookings.length === 0 && calendar) {
                    const freeCrewId = calendar.crews.find(c => !day.bookings.some(b => b.crew_id === c.crew_id))?.crew_id;
                    if (freeCrewId) setCrewId(freeCrewId);
                  }
                }}
                className="rounded-full border border-brand-bright/30 bg-white/60 px-2.5 py-1 text-[11px] font-medium text-brand-dark hover:bg-white/90 transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {!calendarLoading && suggestions.length === 0 && (
          <p className="text-xs text-ui-text3">{t('jobs.noSuggestionsYet', 'No fully-clear windows found this month — pick manually below.')}</p>
        )}
        {calendar?.customer_window_note && (
          <p className="mt-1.5 text-[11px] text-ui-text3">
            Customer: {calendar.customer_window_note}
          </p>
        )}
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-[#CF4148]">{error}</p>
      )}

      {/* Step 1 — Choose crew */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ui-text3">
          {t('jobs.step1ChooseCrew', 'Step 1 · Choose crew')}
        </p>
        {crewsLoading && <p className="text-xs text-ui-text3">{t('common.loading', 'Loading…')}</p>}
        <div className="space-y-1.5">
          {(crews ?? []).map((c) => {
            const bookedDays = crewBookedDays[c.crew_id] ?? 0;
            const color = calendar?.crews.find((cc) => cc.crew_id === c.crew_id)?.color ?? '#7C3AED';
            const isSelected = crewId === c.crew_id;
            return (
              <label
                key={c.crew_id}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                  isSelected ? 'border-brand-dark bg-brand-light/10' : 'border-ui-border hover:border-brand-dark/30 hover:bg-ui-subtle/50'
                }`}
              >
                <input
                  type="radio" name="crew" className="mt-0.5 shrink-0"
                  checked={isSelected} onChange={() => setCrewId(c.crew_id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span className="text-[13px] font-semibold text-ui-text">{c.name}</span>
                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${c.team_type === 'contract' ? 'bg-amber-50 text-amber-700' : 'bg-brand-light/20 text-brand-dark'}`}>
                      {c.team_type === 'contract' ? 'Contract' : 'Internal'}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-ui-text3">
                    {bookedDays === 0
                      ? t('jobs.freeAcrossWindow', 'Free across the selected window')
                      : t('jobs.overlappingJobsCount', '{{count}} overlapping job(s) this month', { count: bookedDays })}
                  </p>
                </div>
              </label>
            );
          })}
          {!crewsLoading && (crews ?? []).length === 0 && (
            <p className="text-[11px] text-ui-text3">{t('jobs.noCrewsYet', 'No Crews exist yet — create one below first.')}</p>
          )}
        </div>
      </div>

      {/* Step 2 — Calendar */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ui-text3">
          {t('jobs.step2PickWindow', 'Step 2 · Pick the date window{{crew}}', {
            crew: selectedCrew ? ` for ${selectedCrew.name}` : '',
          })}
        </p>

        {/* Month navigation */}
        <div className="flex items-center justify-between mb-1.5">
          <button type="button" onClick={() => changeMonth(-1)} className="px-2 py-1 text-ui-text3 hover:text-ui-text">‹</button>
          <span className="text-sm font-semibold text-ui-text">
            {new Date(viewYear, viewMonth - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button type="button" onClick={() => changeMonth(1)} className="px-2 py-1 text-ui-text3 hover:text-ui-text">›</button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2 text-[10px] text-ui-text3">
          {(calendar?.crews ?? []).map((c) => (
            <span key={c.crew_id} className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
              {c.name}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-red-300" />
            {t('jobs.customerUnavailable', 'Customer unavailable')}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-green-400" />
            {t('jobs.customerPreferredTag', 'Customer preferred')}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-ui-border" />
            {t('jobs.restrictedDay', 'Restricted day')}
          </span>
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0.5">
          {WEEKDAY_HEADERS.map((w) => (
            <div key={w} className="pb-1 text-center text-[10px] font-semibold text-ui-text3">{w}</div>
          ))}
          {calendarLoading && (
            <p className="col-span-7 py-3 text-center text-xs text-ui-text3">{t('common.loading', 'Loading…')}</p>
          )}
          {!calendarLoading && calendar && (() => {
            const firstCol = sundayCol(calendar.days[0].weekday);
            const cells: React.ReactNode[] = [];
            for (let i = 0; i < firstCol; i++) cells.push(<div key={`pad-${i}`} />);
            for (const day of calendar.days) {
              const dayNum = Number(day.date.slice(-2));
              const isStart = day.date === scheduledDate;
              const isEnd = day.date === endDate && endDate !== scheduledDate;
              const isInRange = day.date > scheduledDate && day.date < endDate;
              let cellCls = 'flex min-h-[44px] flex-col rounded-lg border p-0.5 cursor-pointer text-left transition-colors ';
              if (day.customer_unavailable_reason) {
                cellCls += 'border-red-200 bg-red-50 dark:bg-red-900/20 ';
              } else if (day.customer_no_work) {
                cellCls += 'border-ui-border bg-ui-subtle opacity-70 ';
              } else if (day.customer_preferred && !isInRange) {
                cellCls += 'border-green-200 bg-green-50/60 dark:bg-green-900/10 ';
              } else if (isInRange) {
                cellCls += 'border-brand-dark/20 bg-brand-light/25 ';
              } else {
                cellCls += 'border-ui-border hover:border-brand-dark/40 ';
              }
              if (isStart) cellCls += 'ring-2 ring-brand-dark ring-offset-1 ';
              else if (isEnd) cellCls += 'ring-2 ring-brand-dark/60 ring-offset-1 ';
              const handleDayClick = () => {
                if (pickMode === 'start') {
                  setScheduledDate(day.date);
                  setEndDate(day.date);
                  setDurationDays(1);
                  setPickMode('end');
                } else {
                  if (day.date >= scheduledDate) {
                    setEndDate(day.date);
                    const d1 = new Date(scheduledDate + 'T00:00:00');
                    const d2 = new Date(day.date + 'T00:00:00');
                    const days = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / 86400000) + 1);
                    setDurationDays(days);
                    setPickMode('start');
                  } else {
                    setScheduledDate(day.date);
                    setEndDate(day.date);
                    setDurationDays(1);
                  }
                }
              };
              cells.push(
                <button
                  key={day.date}
                  type="button"
                  onClick={handleDayClick}
                  className={cellCls}
                >
                  <span className={`text-[10px] font-semibold px-0.5 ${isStart || isEnd ? 'text-brand-dark' : 'text-ui-text'}`}>
                    {dayNum}
                  </span>
                  {isStart && <span className="text-[7px] font-bold text-brand-dark px-0.5 leading-none">START</span>}
                  {isEnd && <span className="text-[7px] font-bold text-brand-dark/60 px-0.5 leading-none">END</span>}
                  {day.crews_free_count > 0 && !day.selected_crew_busy && !day.customer_unavailable_reason && !day.customer_no_work && (
                    <span className="truncate text-[8px] text-green-600 font-medium px-0.5">
                      {day.crews_free_count === calendar.crews.length ? t('jobs.clear', 'Clear') : `${day.crews_free_count} free`}
                    </span>
                  )}
                  {day.selected_crew_busy && (
                    <span className="truncate text-[8px] font-semibold text-[#A8313A] px-0.5">{t('jobs.selectedCrewBusy', 'BUSY')}</span>
                  )}
                  {day.customer_unavailable_reason && (
                    <span className="truncate text-[8px] font-semibold text-red-600 px-0.5">{t('jobs.unavailable', 'UNAVAIL')}</span>
                  )}
                  {day.bookings.map((b) => (
                    <span
                      key={b.crew_id}
                      className="mt-0.5 truncate rounded px-0.5 text-[8px] text-white leading-tight"
                      style={{ backgroundColor: b.color }}
                    >
                      {b.label}
                    </span>
                  ))}
                </button>,
              );
            }
            return cells;
          })()}
        </div>
      </div>

      {/* Date / time / duration inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-medium text-ui-text2 mb-1">{t('jobs.scheduledDate', 'Start date')}</label>
          <CustomDatePicker value={scheduledDate} onChange={(v) => {
            setScheduledDate(v);
            setPickMode('end');
            const newEnd = addDays(v, durationDays);
            setEndDate(newEnd);
          }} />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-ui-text2 mb-1">{t('jobs.endDate', 'End date')}</label>
          <CustomDatePicker value={endDate} onChange={(v) => {
            if (v >= scheduledDate) {
              setEndDate(v);
              const d1 = new Date(scheduledDate + 'T00:00:00');
              const d2 = new Date(v + 'T00:00:00');
              setDurationDays(Math.max(1, Math.round((d2.getTime() - d1.getTime()) / 86400000) + 1));
              setPickMode('start');
            }
          }} />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-ui-text2 mb-1">{t('jobs.startTime', 'Start time')}</label>
          <CustomTimePicker value={startTime} onChange={setStartTime} />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-ui-text2 mb-1">{t('jobs.durationDays', 'Expected duration')}</label>
          <select
            value={Math.min(durationDays, 99)}
            onChange={(e) => {
              const days = Number(e.target.value);
              setDurationDays(days);
              setEndDate(addDays(scheduledDate, days));
              setPickMode('start');
            }}
            className="w-full rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-brand-dark"
          >
            {DURATION_DAYS_OPTIONS.map(({ label, days }) => (
              <option key={days} value={days}>{label}</option>
            ))}
            {durationDays > 5 && (
              <option value={durationDays}>{durationDays} days</option>
            )}
          </select>
        </div>
      </div>

      {/* HOA / quiet hours warning */}
      {isEarlyStart && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400">
          ⚠ {t('jobs.hoaQuietHours', 'Starts before 8:00 AM — may breach HOA quiet hours.')}
        </p>
      )}

      {/* Customer restriction warnings */}
      {selectedDay?.customer_unavailable_reason && (
        <p className="text-[11px] text-[#A8313A]">
          {t('jobs.customerUnavailableReason', 'Customer unavailable this day: {{reason}}', { reason: selectedDay.customer_unavailable_reason })}
        </p>
      )}
      {selectedDay?.customer_no_work && (
        <p className="text-[11px] text-amber-600">{t('jobs.customerNoWorkWarning', 'Customer restricted this weekday from work — confirm before proceeding.')}</p>
      )}
      {selectedDay?.selected_crew_busy && (
        <p className="text-[11px] text-[#A8313A]">{t('jobs.selectedCrewBusyWarning', 'This crew already has a job this day.')}</p>
      )}

      {/* Weather alert */}
      {selectedDay?.weather && (selectedDay.weather.precip_chance ?? 0) > 30 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 px-3 py-2.5 flex gap-2">
          <span className="shrink-0 text-amber-600 text-sm">⚠</span>
          <p className="text-[12px] text-amber-800 dark:text-amber-400">
            {selectedDay.weather.short_forecast
              ? selectedDay.weather.short_forecast
              : t('jobs.weatherAlert', '{{pct}}% chance of precipitation.', { pct: selectedDay.weather.precip_chance })}
            {' '}{t('jobs.weatherTearOffNote', 'Tear-off should not start on a day with afternoon rain.')}
          </p>
        </div>
      )}

      {/* Conflict warning */}
      {conflicts.length > 0 && (
        <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 px-3 py-2 text-[12px] text-amber-700 dark:text-amber-400">
          {t('jobs.conflictWarning', "{{count}} other job(s) overlap this Crew's schedule that day.", { count: conflicts.length })}
        </div>
      )}

      {/* Reminders section */}
      <div>
        <p className="mb-1.5 text-[11px] font-medium text-ui-text2">{t('jobs.reminders', 'Reminders')}</p>
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="flex items-center gap-1 rounded-full border border-ui-border bg-ui-subtle px-2.5 py-1 text-[11px] text-ui-text2">
            {t('jobs.reminder1Week', '1 week before')}
            <button type="button" className="ml-1 text-ui-text3 hover:text-ui-text">×</button>
          </span>
          <span className="flex items-center gap-1 rounded-full border border-ui-border bg-ui-subtle px-2.5 py-1 text-[11px] text-ui-text2">
            {t('jobs.reminder24h', '24h before')}
            <button type="button" className="ml-1 text-ui-text3 hover:text-ui-text">×</button>
          </span>
          <button type="button" className="rounded-full border border-dashed border-ui-border px-2.5 py-1 text-[11px] text-brand-dark hover:bg-brand-light/10">
            + {t('jobs.addReminder', 'Add reminder')}
          </button>
        </div>
        <p className="mt-1 text-[10px] text-ui-text3">
          {t('jobs.reminderConfig', 'Reminder timing and channel (email / SMS) are tenant configurable.')}
        </p>
      </div>

      {/* Submit row */}
      <div className="flex gap-2 pt-1">
        {onCancel && (
          <Button type="button" variant="ghost" disabled={isSaving} onClick={onCancel} className="h-11 min-h-0 flex-1 text-sm">
            {t('workflow.cancel', 'Cancel')}
          </Button>
        )}
        {blocked && !confirmed ? (
          <Button
            type="button"
            disabled={isSaving}
            onClick={() => void handleSubmit(true)}
            className="h-11 min-h-0 flex-1 text-sm"
          >
            {t('jobs.scheduleAnyway', 'Schedule anyway')}
          </Button>
        ) : (
          <Button
            type="button"
            disabled={isSaving || confirmed || !crewId || (isReschedule && !rescheduleReason)}
            onClick={() => void handleSubmit(false)}
            className="h-11 min-h-0 flex-1 text-sm font-semibold"
          >
            {isSaving
              ? t('jobs.scheduling', 'Scheduling…')
              : isReschedule
                ? t('jobs.confirmRescheduleAndNotify', 'Confirm reschedule & Notify')
                : t('jobs.confirmAndNotify', 'Confirm & Notify')}
          </Button>
        )}
      </div>

      {/* Footer note */}
      <p className="text-[10px] text-ui-text3 leading-relaxed">
        {isReschedule
          ? t('jobs.rescheduleFooterNote', 'On confirm: rebooks the window on the Universal Calendar, releases the old crew allocation, re-notifies the crew lead and the customer, and records the reschedule with the reason and the PM on the property timeline. Status is unchanged.')
          : t('jobs.scheduleFooterNote', 'On confirm: books the window on the Universal Calendar, assigns the crew, creates order materials (+1d), confirm materials delivery (+2d) and confirm site access.')}
      </p>
    </div>
  );
};

export default JobSchedulingForm;
